import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { logAnalyticsEvent } from '../utils/firebase';
import { blogPosts } from '../pages/Blogdata';

const BlogSection = styled.section`
  padding: 5rem 0;
  opacity: 0;
  transform: translateY(30px);
  transition: all 0.8s ease;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  justify-items: center;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Category = styled.h4`
  color: ${props => props.theme.colors.primary};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  color: ${props => props.theme.colors.dark};;
  margin-bottom: 2rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const BlogGrid = styled.div`
  display: inline-grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.div`
  cursor: pointer;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.8s ease;
  opacity: 0;
  transform: translateX(50px);
  transition-delay: ${props => props.index * 0.2}s;
  
  &.visible {
    opacity: 1;
    transform: translateX(0);
    
    &:hover {
      transform: translateY(-5px);
    }
  }
`;

const BlogImage = styled.img`
  width: 100%;
  height: 240px;
  object-fit: cover;
  border-radius: 8px;
`;

const BlogContent = styled.div`
  padding: 1.5rem;
`;

const BlogTitle = styled.h3`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.dark};;
  margin-bottom: 1rem;
  font-weight: 600;
  line-height: 1.4;
`;

const BlogExcerpt = styled.p`
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const ReadMoreButton = styled.button`
  background: #B5873A;
  color: white;
  padding: 0.8rem 1.5rem;
  border-radius: 4px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;

  &:hover {
    background: #9E7431;
  }
`;

const ViewAllButton = styled.button`
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.8s ease;
  transition-delay: 0.4s;
  
  ${BlogSection}.visible & {
    opacity: 1;
    transform: translateY(0);
  }

  background: #B5873A;
  color: white;
  padding: 0.8rem 2rem;
  border-radius: 4px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;
  margin: 0 auto;
  display: block;

  &:hover {
    background: #9E7431;
  }
`;

function Blog() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const navigate = useNavigate();



  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
        if (entry.isIntersecting) {
          logAnalyticsEvent('blog_section_viewed');
        }
      },
      { threshold: 0.2 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const featuredPosts = blogPosts.slice(3,5); // Display the 4th and 5th posts as featured

  const handleViewAll = () => {
    navigate('/blog');
  };

  const handleReadMore = (blogId) => {
    navigate(`/blog/${blogId}`);
  };

  return (
    <BlogSection ref={sectionRef} className={isVisible ? 'visible' : ''}>
      <Container>
        <SectionHeader>
          <Category>INSIGHTS AND UPDATE</Category>
          <SectionTitle>Our Recent Blog & News</SectionTitle>
        </SectionHeader>
        

        <BlogGrid>
          {featuredPosts.map((post, index) => (
            <BlogCard key={post.id} index={index} className={isVisible ? 'visible' : ''}>
              <BlogImage src={post.image} alt={post.title} />
              <BlogContent>
                <BlogTitle>{post.title}</BlogTitle>
                <BlogExcerpt>{post.excerpt}</BlogExcerpt>
                <ReadMoreButton onClick={() => handleReadMore(post.id)}>Read More</ReadMoreButton>
              </BlogContent>
            </BlogCard>
          ))}
        </BlogGrid>
        <ViewAllButton onClick={handleViewAll}>View All Blog</ViewAllButton>
      </Container>
    </BlogSection>
  );
}

export default Blog;
